import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import { App, Button, Card, Input, Popconfirm, Select, Switch, Table } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { createPresenter, deletePresenter, updatePresenter } from 'api/events';
import { useI18n } from 'components/I18nProvider';
import { findSnapshotActiveSegment } from 'lib/event-state-merge';
import { fontSizes } from 'pages/SubtitlesPage/subtitles-settings';
import styles from './style.module.scss';
const fontSizeOptions = ['sm', 'md', 'lg', 'xl'];
const resolveOriginalLanguage = (eventSnapshot) => {
    const activeSegment = findSnapshotActiveSegment(eventSnapshot);
    if (activeSegment !== null) {
        return activeSegment.audioLanguage;
    }
    for (const segment of eventSnapshot?.segments ?? []) {
        if (segment.audioLanguage !== '') {
            return segment.audioLanguage;
        }
    }
    return null;
};
const PresentersPanel = ({ eventId, eventSnapshot, publicEventOrigin }) => {
    const { intl } = useI18n();
    const { message } = App.useApp();
    const [newPresenterName, setNewPresenterName] = useState('');
    const [creating, setCreating] = useState(false);
    const [cssDrafts, setCssDrafts] = useState({});
    const presenters = eventSnapshot?.presenters ?? [];
    const originalLanguage = useMemo(() => resolveOriginalLanguage(eventSnapshot), [eventSnapshot]);
    const languageOptions = useMemo(() => {
        const activeSegment = findSnapshotActiveSegment(eventSnapshot);
        const segments = activeSegment !== null
            ? [activeSegment]
            : (eventSnapshot?.segments ?? []);
        const translations = new Set();
        for (const segment of segments) {
            for (const lang of segment.translationSetup.languages) {
                translations.add(lang.iso);
            }
        }
        const options = [];
        if (originalLanguage !== null) {
            options.push({
                value: originalLanguage,
                label: `${originalLanguage} (${intl.formatMessage({ id: 'presenterLanguageOriginal' })})`,
            });
        }
        for (const iso of translations) {
            if (iso === originalLanguage) {
                continue;
            }
            options.push({ value: iso, label: iso });
        }
        return options;
    }, [eventSnapshot, intl, originalLanguage]);
    const buildPresenterUrl = (presenterId) => {
        const origin = publicEventOrigin.endsWith('/') ? publicEventOrigin.slice(0, -1) : publicEventOrigin;
        return `${origin}/?presenterId=${encodeURIComponent(presenterId)}#${eventId}`;
    };
    const patchPresenter = async (presenterId, patch) => {
        try {
            await updatePresenter(eventId, presenterId, patch);
        }
        catch {
            void message.error(intl.formatMessage({ id: 'presenterUpdateFailed' }));
        }
    };
    const handleCreate = async () => {
        const name = newPresenterName.trim();
        if (name === '') {
            return;
        }
        setCreating(true);
        try {
            await createPresenter(eventId, name);
            setNewPresenterName('');
        }
        catch {
            void message.error(intl.formatMessage({ id: 'presenterCreateFailed' }));
        }
        finally {
            setCreating(false);
        }
    };
    const handleDelete = async (presenterId) => {
        try {
            await deletePresenter(eventId, presenterId);
        }
        catch {
            void message.error(intl.formatMessage({ id: 'presenterDeleteFailed' }));
        }
    };
    const getCssDraft = (presenter) => cssDrafts[presenter.id] ?? presenter.customCss;
    const languageSelectValue = (presenter) => presenter.language ?? originalLanguage ?? '';
    const handleLanguageChange = (presenter, value) => {
        if (originalLanguage !== null && value === originalLanguage) {
            void patchPresenter(presenter.id, { language: null });
            return;
        }
        void patchPresenter(presenter.id, { language: value });
    };
    return (_jsxs(Card, { title: intl.formatMessage({ id: 'presentersTitle' }), className: styles.card, children: [presenters.length > 0 ? (_jsx(Table, { className: styles.table, dataSource: presenters, rowKey: "id", pagination: false, size: "small", tableLayout: "fixed", expandable: {
                    expandedRowRender: (presenter) => (_jsxs("div", { className: styles.cssExpandedRow, children: [_jsx(Input.TextArea, { size: "small", rows: 4, value: getCssDraft(presenter), placeholder: intl.formatMessage({ id: 'presenterCustomCssPlaceholder' }), onChange: (e) => {
                                    setCssDrafts((prev) => ({ ...prev, [presenter.id]: e.target.value }));
                                } }), _jsx(Button, { size: "small", onClick: () => {
                                    void patchPresenter(presenter.id, { customCss: getCssDraft(presenter) });
                                }, children: intl.formatMessage({ id: 'presenterApplyCss' }) })] })),
                }, columns: [
                    {
                        title: intl.formatMessage({ id: 'presenterName' }),
                        dataIndex: 'name',
                        width: '22%',
                        render: (name, presenter) => (_jsx("a", { href: buildPresenterUrl(presenter.id), target: "_blank", rel: "noreferrer", children: name })),
                    },
                    {
                        title: intl.formatMessage({ id: 'presenterLanguage' }),
                        dataIndex: 'language',
                        width: '18%',
                        render: (_, presenter) => (_jsx(Select, { size: "small", className: styles.select, value: languageSelectValue(presenter), options: languageOptions, disabled: languageOptions.length === 0, onChange: (value) => handleLanguageChange(presenter, value) })),
                    },
                    {
                        title: intl.formatMessage({ id: 'presenterTts' }),
                        dataIndex: 'textToSpeech',
                        width: '8%',
                        render: (textToSpeech, presenter) => (_jsx(Switch, { size: "small", checked: textToSpeech, onChange: (checked) => {
                                void patchPresenter(presenter.id, { textToSpeech: checked });
                            } })),
                    },
                    {
                        title: intl.formatMessage({ id: 'presenterTheme' }),
                        dataIndex: 'theme',
                        width: '14%',
                        render: (theme, presenter) => (_jsx(Select, { size: "small", className: styles.select, value: theme, options: [
                                { value: 'dark', label: intl.formatMessage({ id: 'presenterThemeDark' }) },
                                { value: 'light', label: intl.formatMessage({ id: 'presenterThemeLight' }) },
                            ], onChange: (value) => {
                                void patchPresenter(presenter.id, { theme: value });
                            } })),
                    },
                    {
                        title: intl.formatMessage({ id: 'presenterFontSize' }),
                        dataIndex: 'fontSize',
                        width: '16%',
                        render: (fontSize, presenter) => (_jsx(Select, { size: "small", className: styles.select, value: fontSize, options: fontSizeOptions.map((size) => ({
                                value: size,
                                label: `${size} (${fontSizes[size]}em)`,
                            })), onChange: (value) => {
                                void patchPresenter(presenter.id, { fontSize: value });
                            } })),
                    },
                    {
                        title: intl.formatMessage({ id: 'presenterTextAlign' }),
                        dataIndex: 'textAlign',
                        width: '14%',
                        render: (textAlign, presenter) => (_jsx(Select, { size: "small", className: styles.select, value: textAlign, options: [
                                { value: 'top', label: intl.formatMessage({ id: 'presenterTextAlignTop' }) },
                                { value: 'bottom', label: intl.formatMessage({ id: 'presenterTextAlignBottom' }) },
                            ], onChange: (value) => {
                                void patchPresenter(presenter.id, { textAlign: value });
                            } })),
                    },
                    {
                        title: '',
                        key: 'actions',
                        width: '8%',
                        render: (_, presenter) => (_jsx(Popconfirm, { title: intl.formatMessage({ id: 'presenterDeleteConfirm' }), onConfirm: () => {
                                void handleDelete(presenter.id);
                            }, children: _jsx(Button, { type: "text", size: "small", danger: true, icon: _jsx(DeleteOutlined, {}), "test-id": "admin-presenter-delete-trigger" }) })),
                    },
                ] })) : null, _jsxs("div", { className: styles.addRow, children: [_jsx(Input, { className: styles.nameInput, value: newPresenterName, placeholder: intl.formatMessage({ id: 'presenterNamePlaceholder' }), onChange: (e) => setNewPresenterName(e.target.value), onPressEnter: () => {
                            void handleCreate();
                        } }), _jsx(Button, { type: "primary", icon: _jsx(PlusOutlined, {}), loading: creating, onClick: () => {
                            void handleCreate();
                        }, "test-id": "admin-presenter-create-button" })] })] }));
};
export default PresentersPanel;
